import { relativeTime } from '../../../conversations/format';
import { ModeBadge, OverrideTypeBadge } from '../../components';

import { readProposedMessage } from './tela9-adapter';

import type { OverrideCardVM } from './tela9-adapter';
import type { Mode } from '../../view-models';
import type React from 'react';

// ── Conversão de design-references/brief-3/shared/cards-3.jsx:1277-1408 ──
// Footer T9-1 = apenas "Ver contexto" (read-only). Ações de decisão chegam na Onda 2.
// Payload lido DEFENSIVAMENTE do contextPayload (Json polimórfico) — campo ausente ⇒ omite a linha.

export interface OverrideRequestCardProps {
  vm: OverrideCardVM;
  requesterName: string | null;
  urgent?: boolean;
  onSeeContext: (vm: OverrideCardVM) => void;
  // Ações de decisão (Onda 2) — gated por cap na página; ausentes ⇒ footer só "Ver contexto".
  canApprove?: boolean;
  canReject?: boolean;
  onApprove?: (vm: OverrideCardVM) => void;
  onReject?: (vm: OverrideCardVM) => void;
}

/** Label do botão Aprovar por tipo de decisão (espelha cards-3.jsx). */
function approveLabel(vm: OverrideCardVM): string {
  if (vm.decision === 'type-1') return 'Editar e enviar';
  if (vm.decision === 'type-5') return 'Aprovar c/ prazo';
  if (vm.dualApproval) return 'Enviar p/ aprovação dupla';
  return 'Aprovar';
}

// ── helpers defensivos sobre o Json polimórfico ──
function obj(payload: unknown): Record<string, unknown> {
  return payload !== null && typeof payload === 'object'
    ? (payload as Record<string, unknown>)
    : {};
}
function str(payload: unknown, key: string): string | null {
  const v = obj(payload)[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v) : null;
}
function strList(payload: unknown, key: string): string[] {
  const v = obj(payload)[key];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
function nested(payload: unknown, key: string): Record<string, unknown> {
  return obj(obj(payload)[key]);
}

const CAPS_LABEL_STYLE: React.CSSProperties = {
  fontSize: 'var(--text-2xs)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--track-caps)',
  fontWeight: 'var(--fw-semibold)',
  marginBottom: 4,
};

function Kv({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="override-card__kv">
      <span>{label}</span>
      {children}
    </div>
  );
}

function OverridePayload({ vm }: { vm: OverrideCardVM }): React.JSX.Element | null {
  const { type, payload } = vm;

  if (type === 'ai_required_validation') {
    const message = readProposedMessage(payload);
    if (message === '') return null;
    const validation = nested(payload, 'validation');
    const validationDetail = typeof validation.detail === 'string' ? validation.detail : null;
    const validationColor = typeof validation.color === 'string' ? validation.color : null;
    return (
      <div className="override-card__payload">
        <div className="tweaks-hint" style={CAPS_LABEL_STYLE}>
          Mensagem proposta
        </div>
        <div
          style={{
            background: 'var(--bg-subtle)',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
            fontStyle: 'italic',
            marginBottom: 6,
          }}
        >
          &quot;{message}&quot;
        </div>
        {validationDetail !== null && (
          <div className="tweaks-hint" style={{ fontSize: 'var(--text-xs)' }}>
            Validação IA:{' '}
            {validationColor !== null && (
              <b style={{ color: 'var(--warning-fg)' }}>{validationColor}</b>
            )}{' '}
            — {validationDetail}
          </div>
        )}
      </div>
    );
  }

  if (type === 'capability_temporaria') {
    const capability = str(payload, 'capability');
    const impact = strList(payload, 'impactPreview');
    if (capability === null && impact.length === 0) return null;
    return (
      <div className="override-card__payload">
        {capability !== null && (
          <Kv label="Capability solicitada">
            <b className="mono">{capability}</b>
          </Kv>
        )}
        {str(payload, 'availableIn') !== null && (
          <Kv label="Disponível em">
            <b>{str(payload, 'availableIn')}</b>
          </Kv>
        )}
        {str(payload, 'duration') !== null && (
          <Kv label="Duração">
            <b>{str(payload, 'duration')}</b>
          </Kv>
        )}
        {impact.length > 0 && (
          <ul className="override-card__impact">
            {impact.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (type === 'legal_capacity_exception') {
    const person = str(payload, 'person');
    const docs = strList(payload, 'docsAttached');
    if (person === null && docs.length === 0) return null;
    return (
      <div className="override-card__payload">
        {person !== null && (
          <Kv label="Person">
            <b>{person}</b>
          </Kv>
        )}
        {str(payload, 'legalCapacity') !== null && (
          <Kv label="LegalCapacity">
            <b style={{ color: 'var(--warning-fg)' }}>{str(payload, 'legalCapacity')}</b>
          </Kv>
        )}
        {str(payload, 'guardian') !== null && <Kv label="Guardian">{str(payload, 'guardian')}</Kv>}
        {docs.length > 0 && (
          <Kv label="Documentação">
            <span className="mono">{docs.join(', ')}</span>
          </Kv>
        )}
      </div>
    );
  }

  if (type === 'conflict_alert_accept') {
    const alertId = str(payload, 'alertId');
    const docs = strList(payload, 'documents');
    if (alertId === null && docs.length === 0) return null;
    return (
      <div className="override-card__payload">
        {alertId !== null && (
          <Kv label="ConflictAlert">
            <span className="mono">#{alertId}</span>
          </Kv>
        )}
        {str(payload, 'category') !== null && <Kv label="Categoria">{str(payload, 'category')}</Kv>}
        {str(payload, 'acceptType') !== null && (
          <Kv label="Tipo de aceite">
            <b>{str(payload, 'acceptType')}</b>
          </Kv>
        )}
        {docs.length > 0 && (
          <Kv label="Documentos">
            <span className="mono" style={{ fontSize: 'var(--text-2xs)' }}>
              {docs.join(', ')}
            </span>
          </Kv>
        )}
      </div>
    );
  }

  if (type === 'modo_operacao') {
    const current = str(payload, 'currentMode');
    const requested = str(payload, 'requestedMode');
    if (current === null && requested === null) return null;
    return (
      <div className="override-card__payload">
        {current !== null && (
          <Kv label="Modo atual">
            <ModeBadge mode={current as Mode} />
          </Kv>
        )}
        {requested !== null && (
          <Kv label="Modo solicitado">
            <ModeBadge mode={requested as Mode} />
          </Kv>
        )}
      </div>
    );
  }

  return null;
}

export function OverrideRequestCard({
  vm,
  requesterName,
  urgent = false,
  onSeeContext,
  canApprove = false,
  canReject = false,
  onApprove,
  onReject,
}: OverrideRequestCardProps): React.JSX.Element {
  const requestedAgo = relativeTime(vm.createdAt).replace(/^há /, '');
  return (
    <article
      className={
        'override-card' +
        (urgent ? ' override-card--urgent' : '') +
        (vm.regulatedOnly ? ' override-card--regulated' : '')
      }
    >
      <header className="override-card__head">
        <OverrideTypeBadge type={vm.type} urgent={urgent} />
        <span style={{ flex: 1 }} />
        <span className="tweaks-hint mono">solicitado há {requestedAgo}</span>
      </header>

      <div className="override-card__solicitante">
        <b>{requesterName ?? '—'}</b>
      </div>

      <div className="override-card__reason">
        <div className="tweaks-hint" style={CAPS_LABEL_STYLE}>
          Razão
        </div>
        &quot;{vm.reason}&quot;
      </div>

      <OverridePayload vm={vm} />

      {vm.regulatedOnly && (
        <div
          className="modal-form__callout modal-form__callout--warn"
          style={{ fontSize: 'var(--text-xs)' }}
        >
          ⚠ Regulated · audit {vm.audit}
          {vm.dualApproval ? ' · aprovação dupla obrigatória' : ' reforçado'}
        </div>
      )}

      <footer className="override-card__foot">
        {canApprove && onApprove !== undefined && (
          <button
            type="button"
            className="btn-ghost-mini btn-ghost-mini--primary"
            onClick={() => onApprove(vm)}
          >
            {approveLabel(vm)}
          </button>
        )}
        {canReject && onReject !== undefined && (
          <button type="button" className="btn-ghost-mini" onClick={() => onReject(vm)}>
            Rejeitar
          </button>
        )}
        <button type="button" className="btn-ghost-mini" onClick={() => onSeeContext(vm)}>
          Ver contexto
        </button>
      </footer>
    </article>
  );
}
